import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ShieldAlert } from 'lucide-react';

import { http } from '@/shared/lib/api';
import { useAuth } from '@/shared/auth/AuthContext';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { Button } from '@/shared/components/ui/button';
import { notify } from '@/shared/lib/notify';

/**
 * Persistent, non-dismissable banner shown on every page while the current
 * session is an admin impersonation grant (PLAN.md item 6) — the only
 * thing standing between an admin and a tenant's full read+write access
 * looking identical to a real operator session.
 */
export function ImpersonationBanner(): JSX.Element | null {
  const { t } = useTranslation();
  const { user, refetchMe } = useAuth();
  const navigate = useNavigate();
  const [ending, setEnding] = useState(false);

  if (!user?.impersonation) return null;

  const handleEnd = async (): Promise<void> => {
    setEnding(true);
    try {
      await http.post('/api/auth/stop-impersonation');
      await refetchMe();
      navigate('/admin/tenants', { replace: true });
    } catch (err) {
      setEnding(false);
      notify.error(err);
    }
  };

  return (
    <Alert variant="warning" className="rounded-none border-x-0 border-t-0">
      <ShieldAlert className="size-4" />
      <AlertDescription className="flex items-center justify-between gap-4">
        <span>
          {user.tenant
            ? t('impersonation.banner.activeFor', { tenant: user.tenant.name })
            : t('impersonation.banner.active')}
        </span>
        <Button
          variant="outline"
          className="shrink-0 border-warning text-warning-text hover:bg-warning/10"
          disabled={ending}
          onClick={() => void handleEnd()}
          data-testid="btn-end-impersonation"
        >
          {t('impersonation.banner.end')}
        </Button>
      </AlertDescription>
    </Alert>
  );
}
